import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {repeat} from 'lit/directives/repeat.js';
import {classMap} from 'lit/directives/class-map.js';
import {msg} from '@lit/localize';
import {property} from 'lit/decorators/property.js';
import {connect} from 'pwa-helpers';

import store from '../../store';
import {selectPriceDataPerModels, selectProductDatas} from '../../selectors';
import {fetchProductData, fetchTranslations} from '../../slices/productDataSlice';
import {setLang, setStatus} from '../../slices/statusSlice';
import {pimText, pimTextDebug} from '../../util/values';
import priceDataTest from './priceDataTest.json';
import {getProductData} from '../../util/getProductData';

@customElement('price-data')
export class PriceData extends connect(store)(LitElement) {

	@property({type: Object})
	priceData = 'detail';

	@property({type: String})
	title = '';

	static styles = [
		css`
			:host {
				container-type: inline-size;
			}

			.priceComponents {
				display: block;
				columns: 2;
				column-gap: 2cqw;
				padding-bottom: 2cqw;
			}

			.component {
				break-inside: avoid;
				margin-bottom: 1.5cqw;
				padding: 0.5cqw;
				font: var(--font-specsheet-print-light);
				font-size: 0.92cqw;
				line-height: 140%;
				width: 100%;
			}

			.Package {
				background-color: #f7f7f7;
			}

			table {
				width: 100%;
				border-collapse: collapse;
			}

			td {
				padding: 0.5cqw 0;
				font: var(--font-specsheet-print-light);
				font-size: 0.92cqw;
				text-align: left;
				border-top: 1px solid #C6C6C6;
			}

			tr:first-of-type td {
				border-top: none;
			}

			th {
				padding: 0.5cqw 0;
				font: var(--font-specsheet-print-bold);
				font-size: 0.92cqw;
				text-align: left;
				border-bottom: 1px solid #000;
			}

			.title {
				font: var(--font-specsheet-print-bold);
				padding: 0.5cqw;
			}

			.price {
			}

			.head {
				overflow: hidden;
				white-space: nowrap;
				max-width: 8cqw;
				text-overflow: ellipsis;
			}

			.description {
				width: 12%;
				color: #999;
			}

			.priceValue {
				width: 8%;
				text-align: right;
			}

			.type {
				width: 20%;
				text-align: right;
			}

			.same {
				opacity: 0;
				border-top: none;
			}

		`
	];

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
		this.doNotShowFeatures = [
			'EB_Control'
		];
	}

	connectedCallback() {
		super.connectedCallback();
		store.dispatch(setStatus({mode: 'external', region: 'ger', lang: 'de'}));
		store.dispatch(fetchProductData({modelId: this.priceData.featureModelId}));
		store.dispatch(fetchTranslations({modelId: this.priceData.featureModelId}));
	}

	getProductDataForValue(attributeProductData, valueId) {
		return attributeProductData?.values?.find(val => val.valueId === valueId);
	}

	stateChanged(state) {
		this.state = state;
		this.lang = state.status.lang;
		this.debug = state.status.debug;

		const productDatas = selectProductDatas(state);
		if (Object.hasOwn(productDatas, this.priceData.featureModelId)) {
			this.translations = productDatas[this.priceData.featureModelId].translations;
			this.productData = productDatas[this.priceData.featureModelId].productData;
			// console.log('translations ' + this.priceData.featureModelId, this.translations);
		}

		const filteredPriceData = this.priceData.conditionalPrices;
		//.filter(conditionalPrice => conditionalPrice.priceInEuro !== 0);

		// Group conditionalPrices by available conditions
		const groupedPrices = filteredPriceData.reduce((acc, price) => {
			// Create a string key based on conditions (e.g., condition1:value1, condition2:value2)

			if (this.productData) {
				// sort based on sequence in PIM data
				price.conditions.sort((a, b) => {
					const apd = getProductData(a.featureId, this.productData)?.displaySequenceUi;
					const apdv = this.getProductDataForValue(apd, a.valueId)?.displaySequenceUi;
					const bpd = getProductData(b.featureId, this.productData)?.displaySequenceUi;
					const bpdv = this.getProductDataForValue(bpd, b.valueId)?.displaySequenceUi;
					return (apd * 1000 + apdv) - (bpd * 1000 + bpdv);
				});
			}

			const conditionKeys = price.conditions.map(condition =>
				`${condition.featureIdSideIndependent}`
			);
			const conditionKey = conditionKeys.join('|');
			const conditionKeyLabels = price.conditions.map(condition =>
				`${this.t(condition.featureIdSideIndependent, 'F')}`
			);

			const valueKeys = price.conditions.map(condition =>
				`${condition.valueId}`
			);
			const valueKey = valueKeys.join('|');

			const valueKeyLabels = price.conditions.map(condition =>
				`${this.t(condition.featureId, 'FV.S', condition.valueId)}`
			);

			// If the key doesn't exist in the accumulator, initialize it
			if (!acc[conditionKey]) {
				acc[conditionKey] = {
					key: conditionKey,
					keys: conditionKey,
					label: conditionKeyLabels,
					type: price.priceType,
					componentPrices: []
				};
			}

			// Add the conditional price to the corresponding group
			// if it is not already in the list
			if (!acc[conditionKey].componentPrices.find(
				componentPrice => componentPrice.valueKey === valueKey &&
					componentPrice.price === price.priceInEuro &&
					componentPrice.description === price.description)) {
				acc[conditionKey].componentPrices.push({
					valueKey: valueKey,
					valueKeys: valueKeys,
					sameValueAsBefore: false,
					label: valueKeyLabels,
					price: price.priceInEuro,
					description: price.description,
					// sourcePrice: price
				});
			}
			return acc;
		}, {});

		// filter specific features
		const groupedPricesArray = Object.values(groupedPrices)
			.filter(group => !this.doNotShowFeatures.includes(group.key));

		// sort price values
		groupedPricesArray.forEach(group => {
			// mark repeating first value
			let currentValueKey = '';
			group.componentPrices.forEach(componentPrice => {
				componentPrice.sameValueAsBefore = (componentPrice.valueKeys[0] === currentValueKey);
				currentValueKey = componentPrice.valueKeys[0];
			});
		});

		// split by price type
		this.packagePrices = groupedPricesArray.filter(group => group.type === 'Package');
		this.surchargePrices = groupedPricesArray.filter(group => group.type === 'Surcharge');

		// console.log(this.packagePrices, this.surchargePrices);
	}

	t(featureId, context, value) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, null, this.translations, value) :
			pimText(this.lang, featureId, context, null, this.translations, value, '', '', '', true);
	}

	renderPrice(price) {
		const priceTypesText = {
			Package: msg('Package'),
			Surcharge: msg('Surcharge'),
		};
		return html`
			<div class="component ${price.type}">
				<table class="priceTable">
					<tr class="title">
						${repeat(price.label, (head) => head, (head) => html`
							<th class="head">${head}</th>`)}
						<th colspan="2" class="type">${priceTypesText[price.type]}</th>
					</tr>
					${repeat(price.componentPrices,
						(componentPrice) => componentPrice.valueKey,
						(componentPrice) => html`
							<tr class="price">
								${repeat(componentPrice.label, (value) => value, (value, index) => html`
									<td class="value ${classMap({same: index === 0 && componentPrice.sameValueAsBefore})}">${value}</td>`)}
								<td class="description">${componentPrice.description}</td>
								<td class="priceValue">${componentPrice.price} €</td>
							</tr>
						`)}
				</table>
			</div>
		`;
	}

	render() {
		return html`
			<div class="priceComponents packagePrices">
				${repeat(this.packagePrices, (price) => price.key, (price) => this.renderPrice(price))}
			</div>
			<div class="priceComponents surchargePrices">
				${repeat(this.surchargePrices, (price) => price.key, (price) => this.renderPrice(price))}
			</div>
		`;
	}

}
